import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api, fileUrl } from "../api/client";
import type { Part } from "../api/types";
import { Button, Input, PageHeader, Toggle } from "../components/Controls";
import { Badge, DemoTag } from "../components/Badge";
import { Column, Table } from "../components/Table";
import { EmptyState } from "../components/EmptyState";
import { ImageLightbox } from "../components/ImageLightbox";
import { fmtDayShort, fmtNum } from "../lib/format";
import { PartFormModal } from "./PartFormModal";

export function modelLabel(p: Part) {
  const m = p.active_model;
  if (!m) return <span className="text-slate-400">Sin modelo</span>;
  return <span className="text-ok">Listo · {fmtDayShort(m.trained_at)} · umbral {fmtNum(m.threshold)}</span>;
}

export default function PartsPage() {
  const navigate = useNavigate();
  const [parts, setParts] = useState<Part[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<Part[]>("/parts", { active: showInactive ? "all" : "true" }).then(setParts).catch(() => undefined).finally(() => setLoading(false));
  }, [showInactive]);
  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? parts.filter((p) => p.part_number.toLowerCase().includes(s) || p.name.toLowerCase().includes(s)) : parts;
  }, [parts, q]);

  const columns: Column<Part>[] = [
    { key: "num", header: "Número", render: (p) => <span className="font-mono font-medium">{p.part_number}</span> },
    { key: "name", header: "Nombre", render: (p) => <span className="inline-flex items-center gap-2">{p.name}{p.is_demo && <DemoTag />}</span> },
    {
      key: "ref", header: "Referencia", render: (p) => p.reference_image_url ? (
        <img src={fileUrl(p.reference_image_url)} alt="" className="ml-auto h-10 w-14 rounded object-cover md:ml-0" onClick={(e) => { e.stopPropagation(); setLightbox(fileUrl(p.reference_image_url)); }} data-testid="part-reference-thumb" />
      ) : <span className="text-slate-400">—</span>,
    },
    { key: "imgs", header: "Fotos OK / NG", render: (p) => <span className="font-mono">{p.image_counts.ok} / {p.image_counts.ng}</span> },
    { key: "model", header: "Modelo activo", render: modelLabel },
    { key: "ops", header: "Operarios", render: (p) => <span className="font-mono">{p.operators_assigned ?? 0}</span> },
    { key: "st", header: "Estado", render: (p) => <Badge tone={p.active ? "ink" : "muted"}>{p.active ? "Activa" : "Inactiva"}</Badge> },
  ];

  return (
    <div className="animate-rise">
      <PageHeader title="Piezas" subtitle="Números de parte, fotos de entrenamiento y modelo activo." actions={<Button onClick={() => setCreating(true)} data-testid="new-part-button"><Plus className="h-4 w-4" /> Nueva pieza</Button>} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por número o nombre" className="pl-9" data-testid="parts-search-input" />
        </div>
        <Toggle checked={showInactive} onChange={setShowInactive} label="Mostrar inactivas" testId="parts-show-inactive-toggle" />
      </div>
      <Table
        testId="parts-table"
        columns={columns}
        rows={rows}
        loading={loading}
        onRowClick={(p) => navigate(`/piezas/${p.id}`)}
        empty={q.trim() && parts.length ? <EmptyState title="Sin resultados" text="Ninguna pieza coincide con la búsqueda." testId="parts-no-results" /> : <EmptyState title="Todavía no hay piezas" text="Crear la primera pieza con sus puntos de inspección (de 1 a 10) y una foto de referencia." action={<Button onClick={() => setCreating(true)} data-testid="empty-new-part-button">Nueva pieza</Button>} />}
      />
      <PartFormModal open={creating} onClose={() => setCreating(false)} onSaved={load} />
      <ImageLightbox src={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
